import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Notifications.css';
import NotificationItem from '../../components/NotificationItem/NotificationItem';
import Pagination from '../../components/Pagination/Pagination';
import { tokenService } from '../../services/auth/tokenService';
import { notificationsService } from '../../services/notifications';
import Loader from '../../components/Loader/Loader';
import Modal from '../../components/Modal/Modal';
import useWebSocket from '../../hooks/useWebSocket';
import { useNavigate } from 'react-router-dom';
import CmsActionModal from '../../components/CmsActionModal/CmsActionModal';
import api from '../../services/api';
import { resolveCmsAction } from '../../utils/resolveCmsAction';
import { formatDateTime } from '../../utils/utils';

const Notifications = () => {
  const { unreadCount, markAllAsRead } = useWebSocket();
  const navigate = useNavigate();

  const [todayNotifications, setTodayNotifications] = useState([]);
  const [previousNotifications, setPreviousNotifications] = useState([]);

  const [page, setPage] = useState(1);
  const itemsPerPage = 12;

  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  // selección de items
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedAll, setSelectedAll] = useState(false);

  // borrar individual con modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetId, setTargetId] = useState(null);

  // borrar masivo con modal
  const [bulkOpen, setBulkOpen] = useState(false);

  // ids en animación
  const [deletingIds, setDeletingIds] = useState(new Set());

  // —— Modal de ACCIÓN (detalle) ——
  const [actionOpen, setActionOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionTitle, setActionTitle] = useState('');
  const [actionGreeting, setActionGreeting] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionDetails, setActionDetails] = useState([]); // [{label, value}]
  const [actionButtons, setActionButtons] = useState({
    showReject: false,
    showApprove: false,
    showMore: false,
    rejectText: 'Cancel',
    approveText: 'Accept',
    moreText: 'More Details',
    onReject: null,
    onApprove: null,
    onMore: null,
  });

  const userId = useMemo(() => Number(tokenService.getUserId()), []);

  const fetchPage = useCallback(async (p) => {
    if (!userId) return;
    setLoading(true);
    try {
      const resp = await notificationsService.getByUser({
        userId,
        page: p,
        items_per_page: itemsPerPage,
      });

      const items = resp?.data?.data ?? [];
      const mapped = items
        .map(n => ({
          id: n.id,
          title: n.title,
          message: n.content,
          timestamp: n.created_at || new Date().toISOString(),
          is_read: !!n.is_read,
          redirection_required: !!n.redirection_required,
          origin: n.origin,
          url: n.url, // api url de info
          data: n.data ?? null, // meta data
          user_type: n.user_type ?? null,
        }))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const todayStr = new Date().toDateString();
      const today = mapped.filter(n => new Date(n.timestamp).toDateString() === todayStr);
      const prev  = mapped.filter(n => new Date(n.timestamp).toDateString() !== todayStr);

      setTodayNotifications(today);
      setPreviousNotifications(prev);

      setTotalItems(resp?.data?.total ?? 'No items');

      // al cambiar de página, limpia selección
      setSelectedIds(new Set());
      setSelectedAll(false);
    } catch (e) {
      console.error('Error loading notifications:', e);
      setTodayNotifications([]);
      setPreviousNotifications([]);
      setTotalItems(0);
      setSelectedIds(new Set());
      setSelectedAll(false);
    } finally {
      setLoading(false);
      markAllAsRead();
    }
  }, [itemsPerPage, userId, unreadCount]);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  // IDs visibles en la página actual
  const visibleIds = useMemo(
    () => [...todayNotifications, ...previousNotifications].map(n => n.id),
    [todayNotifications, previousNotifications]
  );

  // recalc paginación tras borrar
  const recalcPageAfterDelete = useCallback((removedCount) => {
    const prevTotal = totalItems;
    const newTotal = Math.max(0, prevTotal - removedCount);
    const newTotalPages = Math.max(1, Math.ceil(newTotal / itemsPerPage));
    const desiredPage = Math.min(page, newTotalPages);

    const itemsOnCurrentPageBefore = visibleIds.length;
    const itemsOnCurrentPageAfter = itemsOnCurrentPageBefore - removedCount;

    setTotalItems(newTotal);

    if (desiredPage !== page) {
      setPage(desiredPage);
    } else if (itemsOnCurrentPageAfter <= 0) {
      fetchPage(desiredPage);
    }
  }, [totalItems, itemsPerPage, page, visibleIds.length, fetchPage]);

  // toggle selección individual
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  // select-all (solo visibles) ewn la pag actual
  const handleSelectAll = useCallback(() => {
    setSelectedAll(prev => {
      const next = !prev;
      setSelectedIds(next ? new Set(visibleIds) : new Set());
      return next;
    });
  }, [visibleIds]);

  // sincroniza checkbox select-all al cambiar selección manual
  useEffect(() => {
    setSelectedAll(visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id)));
  }, [visibleIds, selectedIds]);

  // marcar leído
  const onMarkedRead = useCallback((id) => {
    notificationsService.markAsRead(id);
    setTodayNotifications(prev => prev.map(x => x.id === id ? { ...x, is_read: true } : x));
    setPreviousNotifications(prev => prev.map(x => x.id === id ? { ...x, is_read: true } : x));
  }, []);

  // pedir confirmación individual
  const onAskDelete = useCallback((id) => {
    setTargetId(id);
    setConfirmOpen(true);
  }, []);

  // quitar de listas
  const removeIdsFromLists = useCallback((idsSet) => {
    setTodayNotifications(prev => prev.filter(x => !idsSet.has(x.id)));
    setPreviousNotifications(prev => prev.filter(x => !idsSet.has(x.id)));
  }, []);

  // confirmar individual
  const handleConfirmDelete = useCallback(async () => {
    const id = targetId;
    setConfirmOpen(false);
    setTargetId(null);
    if (!id || !userId) return;

    try {
      await notificationsService.deleteById(id);

      setDeletingIds(prev => new Set(prev).add(id));

      setTimeout(() => {
        removeIdsFromLists(new Set([id]));
        setDeletingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
        setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });

        recalcPageAfterDelete(1);
      }, 300); // debe coincidi con la transición fade-out del css
    } catch (e) {
      console.error('delete failed', e);
    }
  }, [targetId, userId, removeIdsFromLists, recalcPageAfterDelete]);

  const handleCancelDelete = () => { setConfirmOpen(false); setTargetId(null); };

  // borrar MASIVO
  const openBulkModal = () => setBulkOpen(true);
  const closeBulkModal = () => setBulkOpen(false);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (!userId || selectedIds.size === 0) return;
    closeBulkModal();

    const idsArr = Array.from(selectedIds);

    try {
      await notificationsService.deleteMultiple(userId, idsArr);

      setDeletingIds(prev => {
        const n = new Set(prev);
        idsArr.forEach(id => n.add(id));
        return n;
      });

      setTimeout(() => {
        removeIdsFromLists(new Set(idsArr));
        setDeletingIds(new Set());
        setSelectedIds(new Set());

        recalcPageAfterDelete(idsArr.length);
      }, 300);
    } catch (e) {
      console.error('bulk delete failed', e);
    }
  }, [userId, selectedIds, removeIdsFromLists, recalcPageAfterDelete]);

  const handleOpenAction = useCallback((notif) => {
    return resolveCmsAction(notif, {
      api,
      navigate,
      setActionOpen,
      setActionLoading,
      setActionTitle,
      setActionGreeting,
      setActionMessage,
      setActionDetails,
      setActionButtons,
    });
  }, [navigate]);

  const renderList = (list) =>
    list.map(n => (
      <NotificationItem
        key={n.id}
        id={n.id}
        title={n.title}
        message={n.message}
        dateTime={formatDateTime(new Date(n.timestamp))}
        isRead={n.is_read}
        redirectionRequired={n.redirection_required}
        selected={selectedIds.has(n.id)}
        onToggleSelect={handleToggleSelect}
        onMarkedRead={onMarkedRead}
        onAskDelete={onAskDelete}
        isDeletingExternal={deletingIds.has(n.id)}
        onOpenAction={() => handleOpenAction(n)}
      />
    ));

  const pageIsEmpty = !loading && visibleIds.length === 0;

  return (
    <div className="notifications-page">
      <div className="notifications-container">
      {/* Hoy */}
      <div className="notifications-section">
        <div className="section-header with-actions">
          <h2 className="section-title">Today</h2>

          <div className="header-actions">
            {/* checkbox select-all*/}
            <input
              type="checkbox"
              id="select-all-current"
              checked={selectedAll}
              onChange={handleSelectAll}
              title="Select all on this page"
            />

            {/* eliminar masivo: solo se muestra si hay seleccionados */}
            {selectedIds.size > 0 && (
              <>
                <div
                  className="delete-all-button"
                  onClick={openBulkModal}
                  disabled={selectedIds.size === 0}
                  title="Delete all">
                  <svg width="30" height="30" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M34.7988 15.221L33.0904 18.1764L12.3683 6.21811L14.0767 3.2627L19.27 6.25228L21.5933 5.6202L28.9904 9.89103L29.6225 12.2314L34.7988 15.221ZM10.25 32.4581V11.9581H18.9113L30.75 18.7914V32.4581C30.75 33.3643 30.39 34.2333 29.7493 34.8741C29.1085 35.5148 28.2395 35.8748 27.3333 35.8748H13.6667C12.7605 35.8748 11.8915 35.5148 11.2507 34.8741C10.61 34.2333 10.25 33.3643 10.25 32.4581Z" fill="currentColor"/>
                  </svg>
                  <span className='header-counter'>({selectedIds.size})</span>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="notifications-list">
          {loading && todayNotifications.length === 0 ? <Loader /> : renderList(todayNotifications)}
        </div>
      </div>

        {/* Anteriores */}
        <div className="notifications-section">
          <div className="section-header"><h2 className="section-title">Previous</h2></div>
          <div className="notifications-list">
            {loading && previousNotifications.length === 0 ? <Loader /> : renderList(previousNotifications)}
          </div>
        </div>

        {/* Vacío */}
        {pageIsEmpty && (
          <div style={{
            padding: '2rem', textAlign: 'center', border: '1px dashed var(--color-secondary)',
            borderRadius: '8px', backgroundColor: 'var(--color-background)',
            color: 'var(--color-textSecondary)', fontSize: '0.95rem', fontWeight: 500, margin: '1rem 0'
          }}>
            No notifications
          </div>
        )}

        {/* Paginación */}
        <Pagination
          currentPage={page}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        />

        {/* Modal borrar individual */}
        <Modal
          isOpen={confirmOpen}
          onClose={handleCancelDelete}
          title={{ pre: 'Are you sure you want to', highlight: 'delete', post: 'this notification' }}
          message="This notification will be permanently deleted"
          showCancelButton
          cancelText="Cancel"
          showConfirmButton
          confirmText="Accept"
          onConfirm={handleConfirmDelete}
          padding="4rem 2rem"
        />

        {/* Modal borrar masivo */}
        <Modal
          isOpen={bulkOpen}
          onClose={closeBulkModal}
          title={{ pre: 'Are you sure you want to', highlight: 'delete', post: `these notifications (${selectedIds.size})`}}
          message="All notifications will be permanently deleted"
          showCancelButton
          cancelText="Cancel"
          showConfirmButton
          confirmText="Accept"
          onConfirm={handleConfirmBulkDelete}
          padding="4rem 2rem"
        />

        {/* —— Modal de acción (detalles de la notificación) —— */}
        <CmsActionModal
          isOpen={actionOpen}
          onClose={() => setActionOpen(false)}
          logoSrc={null}
          title={actionTitle}
          greeting={actionGreeting}
          message={actionMessage}
          details={actionDetails}
          loading={actionLoading}
          showReject={actionButtons.showReject}
          rejectText={actionButtons.rejectText}
          onReject={actionButtons.onReject}
          showApprove={actionButtons.showApprove}
          approveText={actionButtons.approveText}
          onApprove={actionButtons.onApprove}
          showMore={actionButtons.showMore}
          moreText={actionButtons.moreText}
          onMore={actionButtons.onMore}
        />
      </div>
    </div>
  );
};

export default Notifications;
